// src/routes/PengaduanKupon.jsx
import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
  Alert,
  CircularProgress,
  FormHelperText,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';

import Field from '../forms/Field';
import api from '../../services/apiClient';

/** ===================== Zod Schema ===================== */
const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const ALLOWED_MIME = new Set(['image/jpeg', 'image/jpg', 'image/png']);
const ALLOWED_EXT = new Set(['.jpg', '.jpeg', '.png']);

const isAllowedImage = (f) => {
  if (!(f instanceof File)) return false;
  const mime = (f.type || '').toLowerCase();
  if (ALLOWED_MIME.has(mime)) return true;
  const dot = f.name.lastIndexOf('.');
  const ext = dot >= 0 ? f.name.slice(dot).toLowerCase() : '';
  return ALLOWED_EXT.has(ext);
};

const pengaduanSchema = z.object({
  nama: z.string().min(1, 'Nama wajib diisi.'),
  email: z
    .string()
    .min(1, 'Email wajib diisi.')
    .email('Format email tidak valid.'),
  telp: z
    .string()
    .min(1, 'No. telp wajib diisi.')
    .regex(/^[0-9+()\-\s]+$/, 'Hanya angka, spasi, +, -, dan tanda kurung.'),
  foto_struk: z
    .instanceof(File, { message: 'Foto struk wajib diunggah.' })
    .refine((f) => f && f.size <= MAX_FILE_SIZE, 'Ukuran file maksimal 2MB.')
    .refine((f) => f && isAllowedImage(f), 'Hanya JPG/JPEG/PNG.'),
});

/** ===================== Field Config ===================== */
const fields = [
  {
    key: 'nama',
    label: 'Nama Lengkap',
    type: 'text',
    placeholder: 'Nama sesuai KTP',
    required: true,
    width: '1',
  },
  {
    key: 'email',
    label: 'Email',
    type: 'email',
    placeholder: 'nama@email.com',
    required: true,
    width: '1',
  },
  {
    key: 'telp',
    label: 'No. Telepon / WhatsApp',
    type: 'text',
    placeholder: 'contoh: 0812xxxxxxx',
    required: true,
    width: '1',
    help: 'Gunakan nomor aktif agar tim kami mudah menghubungi.',
  },
  // foto_struk dirender custom di bawah
];

/** ===================== Helpers ===================== */
const fmtSize = (file) => {
  if (!(file instanceof File)) return '';
  const kb = file.size / 1024;
  const mb = file.size / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(2)} MB` : `${Math.round(kb)} KB`;
};

/** ===================== Komponen Halaman ===================== */
export default function PengaduanKupon() {
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const methods = useForm({
    resolver: zodResolver(pengaduanSchema),
    defaultValues: { nama: '', email: '', telp: '', foto_struk: null },
    mode: 'onTouched',
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    watch,
    control,
    reset,
    setValue,
  } = methods;

  const pickedFile = watch('foto_struk');

  // Buat/bersihkan object URL untuk preview
  useEffect(() => {
    if (pickedFile instanceof File) {
      const url = URL.createObjectURL(pickedFile);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewUrl(null);
    return undefined;
  }, [pickedFile]);

  const fileInfo = useMemo(() => {
    if (!(pickedFile instanceof File)) return null;
    return `${pickedFile.name} — ${fmtSize(pickedFile)}`;
  }, [pickedFile]);

  const onSubmit = async (values) => {
    const formData = new FormData();
    formData.append('nama', values.nama);
    formData.append('email', values.email);
    formData.append('telp', values.telp);
    formData.append('foto_struk', values.foto_struk);

    setLoading(true);
    try {
      const res = await api.post('/indomaret/pengaduan/ikupon', formData, {
        baseURL: `${process.env.REACT_APP_IRACE_API_URL}/api`,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res?.data?.ok === false) throw new Error(`HTTP ${res.status}`);

      alert('Pengaduan berhasil dikirim. Tim kami akan memproses secepatnya.');
      reset(); // kosongkan form
      setPreviewUrl(null); // bersihkan preview
    } catch (e) {
      const message =
        e?.response?.data ||
        e?.message ||
        'Terjadi kesalahan saat mengirim pengaduan. Coba lagi ya.';
      alert(typeof message === 'string' ? message : JSON.stringify(message));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mt: { xs: 8, sm: 5 },
        }}
      >
        <CircularProgress size="4rem" />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
      {/* ===== Header & Subheader ===== */}
      <Box textAlign="center" mb={3}>
        <Typography
          variant="h3"
          sx={{ fontWeight: 800, letterSpacing: 0.2, lineHeight: 1.1, mb: 1 }}
        >
          Form Pengaduan <br /> IKUPON INDOMARET RUN 2025
        </Typography>
        <Typography variant="h5" sx={{ opacity: 0.85 }}>
          Beli tiket sudah, tapi belum dapat ikupon di struk? Tenang, slot kamu
          tetap aman.
        </Typography>
      </Box>

      {/* ===== Narasi ===== */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3 },
          mb: 3,
          border: '1px solid #E5E7EB',
          borderRadius: 2,
          bgcolor: 'rgba(0,0,0,0.02)',
        }}
      >
        <Typography variant="h6" sx={{ mb: 1.5 }}>
          Sobat Indomaret, kalau kamu sudah membeli tiket Indomaret Run 2025 di
          toko Indomaret dan memiliki struk pembayaran, namun tidak menemukan
          ikupon pada ekor struk, jangan khawatir. Slot lari kamu tetap
          terjamin.
        </Typography>
        <Typography variant="h6">
          Silakan isi form di bawah ini dan unggah foto struk kamu agar tim kami
          bisa segera memproses pengaduanmu.
        </Typography>
      </Paper>

      {/* ===== Form ===== */}
      <FormProvider {...methods}>
        <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, md: 3 },
              border: '1px solid #E5E7EB',
              borderRadius: 2,
            }}
          >
            <Stack spacing={2.25}>
              {/* Field biasa */}
              {fields.map((f) => (
                <Field key={f.key} field={f} control={control} />
              ))}

              {/* Field file: custom (tanpa "No file chosen") + PREVIEW */}
              <Controller
                name="foto_struk"
                control={control}
                rules={{ required: true }}
                render={({
                  field: { onChange, ref },
                  fieldState: { error },
                }) => (
                  <Stack spacing={1}>
                    <Typography variant="subtitle2">
                      Unggah Foto Struk{' '}
                      <span style={{ color: '#d32f2f' }}>*</span>
                    </Typography>

                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        sx={{
                          alignSelf: 'flex-start',
                          width: 'auto',
                          px: 2.25,
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                        }}
                      >
                        {pickedFile instanceof File
                          ? 'Ganti Foto'
                          : 'Pilih Foto'}
                        <input
                          type="file"
                          hidden
                          ref={ref}
                          accept=".jpg,.jpeg,.png"
                          //   capture="environment"
                          // izinkan pilih file yang sama agar onChange tetap terpanggil di mobile
                          onClick={(e) => {
                            e.currentTarget.value = '';
                          }}
                          onChange={(e) => {
                            const f = e.target.files?.[0] ?? null;
                            onChange(f);
                          }}
                        />
                      </Button>

                      {/* Actions ketika sudah ada file */}
                      {pickedFile instanceof File && (
                        <Stack direction="row" spacing={1}>
                          {/* <Tooltip title="Lihat">
                            <IconButton
                              aria-label="Lihat gambar"
                              onClick={() => {
                                if (previewUrl)
                                  window.open(
                                    previewUrl,
                                    '_blank',
                                    'noopener,noreferrer'
                                  );
                              }}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip> */}
                          <Tooltip title="Hapus">
                            <IconButton
                              aria-label="Hapus gambar"
                              onClick={() => {
                                setValue('foto_struk', null, {
                                  shouldValidate: true,
                                });
                                setPreviewUrl(null);
                              }}
                            >
                              <DeleteOutlineIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      )}
                    </Stack>

                    {/* Info filename + size */}
                    {pickedFile instanceof File && (
                      <Alert
                        severity="info"
                        variant="outlined"
                        aria-live="polite"
                      >
                        {fileInfo}
                      </Alert>
                    )}

                    {/* PREVIEW THUMBNAIL */}
                    {previewUrl && (
                      <Box
                        sx={{
                          mt: 0.5,
                          p: 1,
                          border: '1px solid #E5E7EB',
                          borderRadius: 2,
                          maxWidth: 420,
                          bgcolor: 'background.paper',
                        }}
                      >
                        <Box
                          component="img"
                          src={previewUrl}
                          alt="Preview struk"
                          sx={{
                            display: 'block',
                            width: '100%',
                            height: 'auto',
                            maxHeight: 260,
                            objectFit: 'contain',
                            borderRadius: 1.5,
                          }}
                        />
                      </Box>
                    )}

                    {error && (
                      <FormHelperText error>
                        {error.message || 'Foto struk wajib diunggah.'}
                      </FormHelperText>
                    )}

                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary' }}
                    >
                      Maksimal 2MB. Hanya JPG/JPEG/PNG.
                    </Typography>
                  </Stack>
                )}
              />

              <Divider sx={{ my: 1 }} />
              <Box display="flex" justifyContent="flex-end" gap={1.5}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isSubmitting}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    textTransform: 'none',
                    fontWeight: 700,
                  }}
                >
                  {isSubmitting ? 'Mengirim…' : 'Kirim Pengaduan'}
                </Button>
              </Box>
            </Stack>
          </Paper>
        </Box>
      </FormProvider>
    </Container>
  );
}
